import axios, { AxiosRequestConfig } from "./axios.service";
import { headers } from "../_helpers/requestHeaders";
import {
  IApiKey,
  IApiKeyRequest,
  IOAuthFlowAccessTokenResult,
  IPartner,
  IPartnerSite,
} from "app/reducers/apiKeySlice";
import appConstants from "app/_helpers/app.constants";

const requestConfig: AxiosRequestConfig = {
  headers: { ...headers.defaultHeaders(), ...headers.backendApiKeyHeader },
};

const apiKeysService = {
  getAvailablePartners: async (): Promise<IPartner[]> => {
    const uri = `${appConstants.iPosServiceUrl}/internal/venues/partners`;
    console.log("called apiKeysService.getAvailablePartners()");
    try {
      const result = await axios.get<IPartner[]>(uri, requestConfig);
      return result.data;
    } catch (error) {
      console.warn(error);
    }
    return [] as IPartner[];
  },
  getAll: async (venueId: number): Promise<IApiKey[]> => {
    const uri = `${appConstants.iPosServiceUrl}/internal/venues/${venueId}/subscriptions`;
    console.log(`called apiKeysService.getAll(venueId: ${venueId})`);
    try {
      const result = await axios.get<IApiKey[] | null>(uri, requestConfig);
      return result.data;
    } catch (error) {
      console.warn(error);
    }
    return null;
  },
  getById: async (locationId: number): Promise<IApiKey> => {
    const uri = `${appConstants.iPosServiceUrl}/internal/venues/subscriptions/${locationId}`;
    console.log("called apiKeyService.getById");
    try {
      const result = await axios.get<IApiKey>(uri, requestConfig);
      return result.data;
    } catch (error) {
      console.warn(error);
    }
    return null;
  },
  postNewApiKey: async (
    venueId: number,
    newItem: IApiKeyRequest
  ): Promise<{ newApiKey: IApiKey; errorMessage: string }> => {
    const uri = `${appConstants.iPosServiceUrl}/internal/venues/${venueId}/subscriptions`;

    console.log("called apiKeyService.postNewApiKey");
    try {
      var newApiKey: IApiKey = null;
      var errorMessage: string = null;
      await axios
        .post<IApiKey>(uri, newItem, requestConfig)
        .then((result) => {
          newApiKey = result.data;
        })
        .catch(function (error) {
          if (error.response) {
            errorMessage = error.response.data;
          }
        });
      return { newApiKey, errorMessage };
    } catch (error) {
      console.warn(error);
    }
    return null;
  },
  editApiKey: async (
    venueId: number,
    updatedItem: IApiKeyRequest
  ): Promise<{ editedApiKey: IApiKey; errorMessage: string }> => {
    const uri = `${appConstants.iPosServiceUrl}/internal/venues/${venueId}/subscriptions/${updatedItem.id}`;

    console.log("called apiKeysService.editApiKey");
    try {
      var editedApiKey: IApiKey = null;
      var errorMessage: string = null;

      await axios
        .put<IApiKey>(uri, updatedItem, requestConfig)
        .then((result) => {
          editedApiKey = result.data;
        })
        .catch(function (error) {
          if (error.response) {
            errorMessage = error.response.data;
          }
        });

      return { editedApiKey, errorMessage };
    } catch (error) {
      console.warn(error);
    }
    return null;
  },
  deleteApiKey: async (
    venueId: number,
    apiKeyIdToDelete: number
  ): Promise<boolean> => {
    const uri = `${appConstants.iPosServiceUrl}/internal/venues/${venueId}/subscriptions/${apiKeyIdToDelete}`;

    console.log("called apiKeysService.deleteApiKey");
    try {
      await axios.delete<boolean>(uri, requestConfig);
      return true;
    } catch (error) {
      console.warn(error);
    }
    return false;
  },
  buildOAuthFlowUri: (
    venueId: number,
    clientId: string,
    theApiKey: IApiKey
  ): string => {
    console.log("called apiKeysService.buildOAuthFlowUri");
    const redirectUri = encodeURIComponent(`${appConstants.redirectUrl}/oauth`);
    const state = appConstants.generateUuidv4();
    localStorage.setItem(`Integration_Id`, `${theApiKey.id}`);
    localStorage.setItem(`Partner_${venueId}_${theApiKey.id}_state`, state);

    const authSettingsByPartnerName = appConstants.oAuthSettings.find(
      (f) => f.partner.toLowerCase() === theApiKey.posVendorName.toLowerCase()
    );
    if (!authSettingsByPartnerName) {
      console.log(
        `oauth settings missing for partner '${theApiKey.posVendorName}'`
      );
      return null;
    }
    const basePath = `${authSettingsByPartnerName.authBaseUrl}${authSettingsByPartnerName.authUri}`;
    const squareOauthScopes =
      "RESERVATIONS_READ+RESERVATIONS_WRITE+MERCHANT_PROFILE_READ+RESTAURANT_CHECKS_READ+CUSTOMERS_READ+CUSTOMERS_WRITE+GIFTCARDS_READ+GIFTCARDS_WRITE";

    switch (theApiKey.posVendorName.toLocaleLowerCase()) {
      case "kounta":
        return `${basePath}?client_id=${clientId}&redirect_uri=${redirectUri}&state=${encodeURIComponent(
          state
        )}&response_type=code`;
      case "kounta-split":
        return `${basePath}?client_id=${clientId}&redirect_uri=${redirectUri}&state=${encodeURIComponent(
          state
        )}&response_type=code`;
      case "square":
        return `${basePath}?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${squareOauthScopes}&session=false&state=${state}`;
      default:
        return null;
    }
  },
  postOAuthFlowToGetAccessToken: async (
    venueId: number,
    locationId: number,
    code: string
  ): Promise<IOAuthFlowAccessTokenResult> => {
    console.log("called apiKeysService.postOAuthFlowToGetAccessToken");

    const url = `${appConstants.iPosServiceUrl}/internal/venues/${venueId}/locations/${locationId}/partners/oauth`;
    const redirect_uri = `${appConstants.redirectUrl}/oauth`;
    try {
      //
      const result = await axios.post<IOAuthFlowAccessTokenResult>(
        url,
        {
          code,
          redirectUrl: redirect_uri,
        },
        requestConfig
      );
      return result.data;
    } catch (error) {
      console.warn(error);
    }
    return {} as IOAuthFlowAccessTokenResult;
  },
  getPartnerSites: async (
    venueId: number,
    locationId: number
  ): Promise<IPartnerSite[]> => {
    console.log("called apiKeysService.getPartnerSites");

    const url = `${appConstants.iPosServiceUrl}/internal/venues/${venueId}/locations/${locationId}/partners/sites`;
    try {
      //
      const result = await axios.get<IPartnerSite[]>(url, requestConfig);
      return result.data;
    } catch (error) {
      console.warn(error);
    }
    return [] as IPartnerSite[];
  },
  updateApiKeyLocationId: async (
    venueId: number,
    updatedItem: IApiKeyRequest
  ): Promise<{ editedApiKey: IApiKey; errorMessage: string }> => {
    console.log("called apiKeysService.updateApiKeyLocationId");
    const uri = `${appConstants.iPosServiceUrl}/internal/venues/${venueId}/locations/${updatedItem.id}/partners/update-site`;

    try {
      var editedApiKey: IApiKey = null;
      var errorMessage: string = null;

      var req = {
        id: updatedItem.id,
        posVendorName: updatedItem.posVendorName,
        locationId: updatedItem.locationId,
        companyId: updatedItem.companyId,
      };
      await axios
        .patch<IApiKey>(uri, req, requestConfig)
        .then((result) => {
          editedApiKey = result.data;
        })
        .catch(function (error) {
          if (error.response) {
            errorMessage = error.response.data;
          }
        });

      return { editedApiKey, errorMessage };
    } catch (error) {
      console.warn(error);
    }
    return null;
  },
};

export default apiKeysService;
